import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  CircularProgress,
  Alert,
  Paper,
  Divider,
  Avatar,
  Chip,
  Drawer,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  useTheme,
  AppBar,
  Toolbar,
  Button,
  InputBase,
  IconButton,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import supabase from '../supabaseClient';
import {
  QuestionAnswer,
  AccessTime,
  Home,
  Category,
  Bookmark,
  TrendingUp,
  Star,
  Settings,
  RemoveRedEye,
  Search as SearchIcon,
  Notifications,
  AccountCircle,
  Code,
  MenuBook,
  School,
  Forum,
} from '@mui/icons-material';
import logo from '../assets/warrili.png';

// Custom styled components
const StyledPaper = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(3),
  marginBottom: theme.spacing(2),
  transition: 'transform 0.2s ease-in-out',
  cursor: 'pointer',
  backgroundColor: '#1a1a1a',
  color: '#ffffff',
  '&:hover': {
    transform: 'translateY(-2px)',
    boxShadow: '0 8px 16px rgba(0, 179, 89, 0.2)',
  },
}));

const SidebarContainer = styled(Box)(({ theme }) => ({
  width: 300,
  backgroundColor: '#1a1a1a',
  borderLeft: '1px solid #333',
  padding: theme.spacing(2),
  height: '100vh',
  position: 'fixed',
  right: 0,
  top: 0,
  overflowY: 'auto',
}));

const ViewCount = styled(Box)(({ theme }) => ({
  color: '#00b359',
  fontSize: '14px',
  display: 'flex',
  alignItems: 'center',
  gap: '4px',
  '& svg': {
    fontSize: '16px',
  },
}));

const SidebarQuestion = styled(Box)(({ theme }) => ({
  padding: theme.spacing(1.5),
  borderBottom: '1px solid #333',
  '&:hover': {
    backgroundColor: 'rgba(0, 179, 89, 0.05)',
  },
  cursor: 'pointer',
}));

const MainContent = styled(Box)(({ theme }) => ({
  marginLeft: 240,
  backgroundColor: '#121212',
  minHeight: '100vh',
  padding: theme.spacing(3),
}));

const CategoryChip = styled(Chip)(({ theme }) => ({
  backgroundColor: '#00b359',
  color: '#000000',
  fontWeight: 'bold',
  '&:hover': {
    backgroundColor: '#00cc66',
  },
}));

const StyledAppBar = styled(AppBar)(({ theme }) => ({
  backgroundColor: '#000000',
  borderBottom: '1px solid #333',
}));

const SearchBar = styled('div')(({ theme }) => ({
  position: 'relative',
  backgroundColor: '#1a1a1a',
  borderRadius: theme.shape.borderRadius,
  marginRight: theme.spacing(2),
  marginLeft: theme.spacing(3),
  width: '300px',
  '&:hover': {
    backgroundColor: '#262626',
  },
}));

const SearchIconWrapper = styled('div')(({ theme }) => ({
  padding: theme.spacing(0, 2),
  height: '100%',
  position: 'absolute',
  pointerEvents: 'none',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  color: '#00b359',
}));

const StyledInputBase = styled(InputBase)(({ theme }) => ({
  color: '#ffffff',
  width: '100%',
  '& .MuiInputBase-input': {
    padding: theme.spacing(1, 1, 1, 0),
    paddingLeft: `calc(1em + ${theme.spacing(4)})`,
    width: '100%',
  },
}));

const Questions = () => {
  const [questions, setQuestions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState('All');

  const [popularQuestions] = useState([
    {
      id: 1,
      title: "What is the difference between == and === in JavaScript?",
      views: "2.1m",
      votes: 15234,
      category: "JavaScript"
    },
    {
      id: 2,
      title: "How to center a div with CSS?",
      views: "1.8m",
      votes: 12453,
      category: "CSS"
    },
    {
      id: 3,
      title: "What are React Hooks?",
      views: "982k",
      votes: 8765,
      category: "React"
    },
    {
      id: 4,
      title: "How to handle async operations in JavaScript?",
      views: "756k",
      votes: 6543,
      category: "JavaScript"
    },
    {
      id: 5,
      title: "Best practices for REST API design",
      views: "543k",
      votes: 4321,
      category: "API"
    },
  ]);

  useEffect(() => {
    const fetchQuestions = async () => {
      try {
        setLoading(true);
        const { data, error } = await supabase
          .from('question2')
          .select('*')
          .order('created_at', { ascending: false });

        if (error) throw error;
        setQuestions(data);
      } catch (err) {
        console.error('Error fetching questions:', err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchQuestions();
  }, []);

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  const Sidebar = () => (
    <SidebarContainer>
      <Typography variant="h5" sx={{ 
        color: '#00b359', 
        mb: 4, 
        fontWeight: 'bold',
        textAlign: 'center' 
      }}>
        Q&A Platform
      </Typography>
      <List>
        {popularQuestions.map((item, index) => (
          <ListItem 
            button 
            key={index}
            sx={{
              mb: 1,
              borderRadius: 1,
              '&:hover': {
                backgroundColor: 'rgba(0, 179, 89, 0.1)',
              },
            }}
          >
            <ListItemIcon sx={{ color: item.color }}>
              {item.icon}
            </ListItemIcon>
            <ListItemText 
              primary={item.text} 
              sx={{ 
                '& .MuiListItemText-primary': { 
                  color: '#ffffff',
                  fontWeight: 500,
                } 
              }}
            />
          </ListItem>
        ))}
      </List>
    </SidebarContainer>
  );

  const PopularQuestionsSidebar = () => (
    <SidebarContainer>
      <Typography 
        variant="h6" 
        sx={{ 
          color: '#00b359',
          mb: 2,
          fontWeight: 'bold',
          borderBottom: '2px solid #00b359',
          pb: 1
        }}
      >
        Most Viewed Questions
      </Typography>
      
      {popularQuestions.map((question) => (
        <SidebarQuestion key={question.id}>
          <Typography 
            variant="body2" 
            sx={{ 
              color: '#00b359',
              mb: 1,
              '&:hover': {
                color: '#00cc66',
              }
            }}
          >
            {question.title}
          </Typography>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <ViewCount>
              <RemoveRedEye sx={{ color: '#00b359' }} />
              {question.views} views
            </ViewCount>
            <Chip 
              label={question.category}
              size="small"
              sx={{
                backgroundColor: 'rgba(0, 179, 89, 0.1)',
                color: '#00b359',
                fontSize: '11px'
              }}
            />
          </Box>
        </SidebarQuestion>
      ))}
    </SidebarContainer>
  );

  const Navbar = () => (
    <StyledAppBar position="fixed">
      <Toolbar>
        <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
          {/* Logo */}
          <img 
            src={logo} 
            alt="Logo" 
            style={{ height: '40px', marginRight: '20px' }} 
          />

          {/* Navigation Items */}
          <Button 
            startIcon={<Code />} 
            sx={{ 
              color: '#00b359', 
              mr: 2,
              '&:hover': { backgroundColor: 'rgba(0, 179, 89, 0.1)' }
            }}
          >
            Code Lab
          </Button>
          <Button 
            startIcon={<MenuBook />} 
            sx={{ 
              color: '#00b359', 
              mr: 2,
              '&:hover': { backgroundColor: 'rgba(0, 179, 89, 0.1)' }
            }}
          >
            Documentation
          </Button>
          <Button 
            startIcon={<School />} 
            sx={{ 
              color: '#00b359', 
              mr: 2,
              '&:hover': { backgroundColor: 'rgba(0, 179, 89, 0.1)' }
            }}
          >
            Tutorials
          </Button>
          <Button 
            startIcon={<Forum />} 
            sx={{ 
              color: '#00b359',
              mr: 2,
              '&:hover': { backgroundColor: 'rgba(0, 179, 89, 0.1)' }
            }}
          >
            Community
          </Button>

          {/* Search Bar */}
          <SearchBar>
            <SearchIconWrapper>
              <SearchIcon />
            </SearchIconWrapper>
            <StyledInputBase
              placeholder="Search…"
              inputProps={{ 'aria-label': 'search' }}
            />
          </SearchBar>

          {/* Right Side Icons */}
          <Box sx={{ flexGrow: 1 }} />
          <IconButton 
            sx={{ 
              color: '#00b359',
              mr: 1,
              '&:hover': { backgroundColor: 'rgba(0, 179, 89, 0.1)' }
            }}
          >
            <Notifications />
          </IconButton>
          <IconButton 
            sx={{ 
              color: '#00b359',
              '&:hover': { backgroundColor: 'rgba(0, 179, 89, 0.1)' }
            }}
          >
            <AccountCircle />
          </IconButton>
        </Box>
      </Toolbar>
    </StyledAppBar>
  );

  if (loading) {
    return (
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        minHeight: '100vh',
        backgroundColor: '#121212',
      }}>
        <CircularProgress sx={{ color: '#00b359' }} />
      </Box>
    );
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column' }}>
      <Navbar />
      <Box sx={{ 
        display: 'flex',
        mt: '64px', // Add margin-top to account for fixed navbar
      }}>
        <MainContent sx={{ marginRight: '300px' }}>
          <Typography 
            variant="h4" 
            sx={{ 
              color: '#00b359',
              mb: 3,
              fontWeight: 'bold',
            }}
          >
            Community Questions
          </Typography>
          
          {questions.map((question) => (
            <StyledPaper key={question.id} elevation={3}>
              <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                <Avatar 
                  sx={{ 
                    bgcolor: '#00b359',
                    color: '#000000',
                    width: 48,
                    height: 48,
                    fontWeight: 'bold',
                  }}
                >
                  {question.user_name ? question.user_name[0].toUpperCase() : 'Q'}
                </Avatar>
                <Box sx={{ flexGrow: 1 }}>
                  <Typography 
                    variant="h6" 
                    sx={{ 
                      color: '#00b359',
                      mb: 1,
                      fontWeight: 500,
                    }}
                  >
                    {question.title}
                  </Typography>
                  <Typography 
                    variant="body1" 
                    sx={{ 
                      color: '#ffffff',
                      opacity: 0.8,
                      mb: 2,
                    }}
                  >
                    {question.content}
                  </Typography>
                  <Box sx={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    gap: 2,
                    flexWrap: 'wrap'
                  }}>
                    <CategoryChip 
                      icon={<AccessTime sx={{ color: '#000000' }} />}
                      label={formatDate(question.created_at)}
                      size="small"
                    />
                    <CategoryChip
                      icon={<QuestionAnswer sx={{ color: '#000000' }} />}
                      label={`${question.answers_count || 0} answers`}
                      size="small"
                    />
                    {question.category && (
                      <CategoryChip
                        label={question.category}
                        size="small"
                      />
                    )}
                  </Box>
                </Box>
              </Box>
            </StyledPaper>
          ))}
        </MainContent>
        <PopularQuestionsSidebar />
      </Box>
    </Box>
  );
};

export default Questions;